import ExcelJS from 'exceljs';
import {
  bordeFino,
  fill,
  fuente,
  FMT_MONEDA,
  XLSX_COLOR,
  XLSX_FILL,
} from '@/shared/lib/xlsxStyle';
import type { ResumenCajaRow } from '../api';
import { etiquetaCaja } from '@/shared/lib/cajasEfectivo';

/**
 * Export del resumen de Tesorería, con el mismo lenguaje visual que el
 * "Inventario valorizado" que definió el cliente: tarjetas arriba, tabla con
 * fila de TOTAL en `SUBTOTAL` (sigue al filtro) y notas al pie.
 *
 * Una diferencia de fondo con Inventario: acá **no hay un total único**. En
 * AR conviven cajas en pesos y en dólares, y sumarlas daría un número sin
 * sentido — por eso hay una tarjeta por moneda y la fila TOTAL sólo cuenta
 * cuando el filtro deja una sola moneda a la vista. El pie lo aclara.
 */

const FILA_ENCABEZADO = 6;
const FILA_TOTAL = 7;
const FILA_DATOS = 8;

const COLUMNAS = [
  { titulo: 'Caja', ancho: 28, kind: 'texto' },
  { titulo: 'Moneda', ancho: 10, kind: 'texto' },
  { titulo: 'Saldo calculado', ancho: 18, kind: 'moneda' },
  { titulo: 'Ingresos período', ancho: 18, kind: 'moneda' },
  { titulo: 'Egresos período', ancho: 18, kind: 'moneda' },
  { titulo: 'Último cierre', ancho: 16, kind: 'texto' },
] as const;

export type ContextoResumenTesoreria = {
  /** 'Argentina' / 'México'. */
  pais: string;
  /** `dd/mm/yyyy`. */
  fecha: string;
  /** Cotización usada cuando la vista está en dólares. */
  cotizacion: { casa: string; fecha: string; valor: number } | null;
};

export function buildResumenTesoreriaWorkbook(
  cajas: ResumenCajaRow[],
  ctx: ContextoResumenTesoreria,
): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Fornitalia';
  wb.created = new Date();
  const ws = wb.addWorksheet('Tesorería');
  ws.columns = COLUMNAS.map((c) => ({ width: c.ancho }));

  const titulo = ws.getCell('A1');
  titulo.value = 'Tesorería — saldos por caja';
  titulo.font = fuente({ size: 16, bold: true });
  ws.getRow(1).height = 24;

  const sub = ws.getCell('A2');
  sub.value =
    `${ctx.pais} · al ${ctx.fecha} · ${cajas.length} caja${cajas.length === 1 ? '' : 's'}` +
    (ctx.cotizacion
      ? ` · convertido a USD al ${ctx.cotizacion.casa} del ${ctx.cotizacion.fecha}`
      : '');
  sub.font = fuente({ size: 10, color: XLSX_COLOR.gris });
  ws.getRow(2).height = 15.75;
  ws.getRow(3).height = 6;

  // --- Tarjetas: un total por moneda ---
  const porMoneda = new Map<string, number>();
  for (const c of cajas) porMoneda.set(c.moneda, (porMoneda.get(c.moneda) ?? 0) + c.saldo);
  ws.getRow(4).height = 13.5;
  ws.getRow(5).height = 24;
  [...porMoneda.entries()].forEach(([moneda, total], i) => {
    const c = 1 + i * 2;
    const rotulo = ws.getRow(4).getCell(c);
    rotulo.value = `TOTAL ${moneda}`;
    rotulo.font = fuente({ size: 8, bold: true, color: XLSX_COLOR.gris });
    rotulo.fill = fill(XLSX_FILL.suave);
    rotulo.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    rotulo.border = { left: bordeFino, right: bordeFino, top: bordeFino };

    const valor = ws.getRow(5).getCell(c);
    valor.value = total;
    valor.numFmt = FMT_MONEDA;
    valor.font = fuente({
      size: 13,
      bold: true,
      color: total < 0 ? XLSX_COLOR.ambar : XLSX_COLOR.texto,
    });
    valor.fill = fill(XLSX_FILL.suave);
    valor.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    valor.border = { left: bordeFino, right: bordeFino, bottom: bordeFino };
  });

  // --- Encabezado ---
  const enc = ws.getRow(FILA_ENCABEZADO);
  enc.height = 21.75;
  COLUMNAS.forEach((c, i) => {
    const celda = enc.getCell(i + 1);
    celda.value = c.titulo;
    celda.font = fuente({ size: 9, bold: true });
    celda.fill = fill(XLSX_FILL.encabezado);
    celda.alignment = {
      horizontal: c.kind === 'texto' ? 'left' : 'right',
      vertical: 'middle',
    };
    celda.border = { bottom: bordeFino };
  });

  // --- TOTAL (SUBTOTAL, sigue al filtro) ---
  const ultimaFila = FILA_DATOS + Math.max(cajas.length, 1) - 1;
  const filaTotal = ws.getRow(FILA_TOTAL);
  filaTotal.height = 21.75;
  ws.mergeCells(FILA_TOTAL, 1, FILA_TOTAL, 2);
  const rotuloTotal = filaTotal.getCell(1);
  rotuloTotal.value = 'TOTAL  ·  se recalcula al filtrar';
  rotuloTotal.font = fuente({ size: 10, bold: true });
  rotuloTotal.alignment = { horizontal: 'right', vertical: 'middle' };
  COLUMNAS.forEach((c, i) => {
    const celda = filaTotal.getCell(i + 1);
    celda.fill = fill(XLSX_FILL.total);
    celda.border = { top: bordeFino, bottom: bordeFino };
    if (c.kind !== 'moneda') return;
    const L = String.fromCharCode(65 + i);
    celda.value = {
      formula: `SUBTOTAL(109,${L}${FILA_DATOS}:${L}${ultimaFila})`,
      date1904: false,
    };
    celda.numFmt = FMT_MONEDA;
    celda.font = fuente({ size: 10, bold: true });
    celda.alignment = { horizontal: 'right', vertical: 'middle' };
  });

  // --- Datos ---
  cajas.forEach((caja, idx) => {
    const row = ws.getRow(FILA_DATOS + idx);
    row.height = 15.75;
    const valores: Array<string | number | null> = [
      etiquetaCaja(caja.caja),
      // La marca de convertida viaja acá: en la vista USD conviven cajas
      // convertidas y cajas que siempre operaron en dólares.
      caja.convertidaDesde ? `${caja.moneda} (de ${caja.convertidaDesde.moneda})` : caja.moneda,
      caja.saldo,
      caja.ingresos,
      caja.egresos,
      caja.ultimoCierre ?? 'Sin cierres',
    ];
    COLUMNAS.forEach((c, i) => {
      const celda = row.getCell(i + 1);
      celda.value = valores[i];
      celda.font = fuente({ size: 10 });
      celda.alignment = {
        horizontal: c.kind === 'texto' ? 'left' : 'right',
        vertical: 'middle',
      };
      celda.border = { bottom: bordeFino };
      if (c.kind === 'moneda') celda.numFmt = FMT_MONEDA;
      if (idx % 2 === 1) celda.fill = fill(XLSX_FILL.zebra);
    });
  });

  ws.autoFilter = {
    from: { row: FILA_ENCABEZADO, column: 1 },
    to: { row: ultimaFila, column: COLUMNAS.length },
  };
  ws.views = [
    { state: 'frozen', xSplit: 1, ySplit: FILA_TOTAL, topLeftCell: `B${FILA_DATOS}` },
  ];

  // --- Cómo leer ---
  const filaNotas = ultimaFila + 2;
  const notas = ws.getCell(filaNotas, 1);
  notas.value = 'Cómo leer este reporte';
  notas.font = fuente({ size: 10, bold: true });
  [
    '· Ingresos, egresos y saldo son del período activo de cada caja: desde su última apertura, no del mes calendario.',
    '· "Sin cierres" = la caja nunca tuvo un cierre; su período activo arranca en el primer movimiento.',
    ctx.cotizacion
      ? `· Las cajas en moneda local se convirtieron a USD al ${ctx.cotizacion.casa} del ${ctx.cotizacion.fecha} ($${ctx.cotizacion.valor} por USD); las que ya operaban en dólares se muestran sin convertir.`
      : '· Cada caja se muestra en su propia moneda; la fila TOTAL sólo tiene sentido si el filtro deja una sola moneda a la vista.',
    '· La fila TOTAL usa SUBTOTAL: se recalcula al usar los filtros del encabezado.',
  ].forEach((texto, i) => {
    const celda = ws.getCell(filaNotas + 1 + i, 1);
    celda.value = texto;
    celda.font = fuente({ size: 9, color: XLSX_COLOR.gris });
  });

  return wb;
}
